import { useQuery } from '@tanstack/react-query'

import {
  fetchAdjustmentOptions,
  fetchFinishedAlterations,
} from '../api/alterationsApi'
import {
  buildAlterationRows,
  getLatestFinishedAdjustments,
  type AlterationRow,
} from '../utils/wardrobeAlterations'

import type { EnvironmentWithChina } from '../App'

const STALE_TIME = 5 * 60 * 1000
// The option catalogue is reference data shared by every item.
const OPTIONS_STALE_TIME = 60 * 60 * 1000

/**
 * Size alterations of one wardrobe item. Any failure (search, option lookup,
 * timeout) resolves to no rows, so the item panel renders as it does without
 * alterations.
 */
export function useWardrobeAlterations({
  epc,
  env,
  authToken,
  language,
}: {
  epc?: string | null
  env?: EnvironmentWithChina
  authToken?: string
  language: string
}): AlterationRow[] {
  const normalizedEpc = epc?.trim()
  const enabled = Boolean(normalizedEpc && env)

  const alterations = useQuery({
    queryKey: ['wardrobe-alterations', env, normalizedEpc],
    queryFn: () => fetchFinishedAlterations(normalizedEpc!, env!, authToken),
    enabled,
    staleTime: STALE_TIME,
    retry: false,
  })

  const adjustments = alterations.data
    ? getLatestFinishedAdjustments(alterations.data)
    : []

  // The catalogue is only needed once there is something to label.
  const options = useQuery({
    queryKey: ['adjustment-options', env],
    queryFn: () => fetchAdjustmentOptions(env!, authToken),
    enabled: enabled && adjustments.length > 0,
    staleTime: OPTIONS_STALE_TIME,
    retry: false,
  })

  if (!options.data) return []
  return buildAlterationRows(adjustments, options.data, language)
}
